import React from 'react';
import { combinedSchema } from './schemas';
import { Formik, Form } from 'formik';
import { Container, Stack, Heading, Box, useDisclosure, Flex, Spacer, Collapse, Button, Divider, Icon } from '@chakra-ui/react';
import { FiChevronDown, FiChevronUp } from 'react-icons/fi';
import { languagesEN, titlesEN } from '../../../shared/components/functions';
import { useLookupStore } from '../../../context/useStore';
import { TextInput, SelectInput, TextareaInput, CheckboxInput, BusinessTypeCheckboxInput, fairOutlineButtonProps } from './forms';

export default function CombinedForm({ SubmitButton, handleSubmit, prefillData }) {
  const { contacttitels, countries, languages } = useLookupStore();
  const langEN = languagesEN(languages);
  const titles = titlesEN(contacttitels);

  const { isOpen, onToggle, onOpen } = useDisclosure();

  const allValues = {
    companyNAAM: '',
    STRAAT_NR: '',
    WOONPL: '',
    POSTNR: '',
    companyLandID: window.location.href.includes('perfectmoose') ? '468' : '300',
    companyTaalID: '3',
    personeelsbestand: '',
    companyGSM: '',
    companyTEL: '',
    'web-site': '',
    companyEmail: '',
    companyOPMERKING: '',
    KlantStatusID: '5',
    NAAM: '',
    DEPT: '',
    AanspreektitelID: '1',
    TaalID: '3',
    'E-mail': '',
    GSM: '',
    TEL: '',
    OPMERKING: '',
    KLANR: '',
    interestedIn: [],
    businessType: [],
    otherBusinessType: '',
    talkedTo: ''
  };

  return (
    <Formik initialValues={allValues} validationSchema={combinedSchema} onSubmit={(values) => handleSubmit(values)}>
      {(formikProps) => {
        const { setValues } = formikProps;

        React.useEffect(() => {
          if (!prefillData) return;

          setValues((currentValues) => {
            const nextValues = { ...currentValues };

            const assignIfPresent = (field, value) => {
              if (value === undefined || value === null) return;
              if (Array.isArray(value)) {
                nextValues[field] = value;
                return;
              }

              if (typeof value === 'string') {
                const trimmed = value.trim();
                if (trimmed !== '') {
                  nextValues[field] = trimmed;
                }
                return;
              }

              nextValues[field] = value;
            };

            const fieldsToAssign = [
              'companyNAAM',
              'STRAAT_NR',
              'WOONPL',
              'POSTNR',
              'companyTaalID',
              'personeelsbestand',
              'companyGSM',
              'companyTEL',
              'web-site',
              'companyEmail',
              'companyOPMERKING',
              'KlantStatusID',
              'NAAM',
              'DEPT',
              'AanspreektitelID',
              'TaalID',
              'E-mail',
              'GSM',
              'TEL',
              'OPMERKING',
              'interestedIn',
              'businessType',
              'otherBusinessType',
              'talkedTo'
            ];

            fieldsToAssign.forEach((field) => assignIfPresent(field, prefillData[field]));

            if (prefillData.companyLandID) {
              assignIfPresent('companyLandID', prefillData.companyLandID);
            } else if (prefillData.companyLand) {
              const normalisedCountry = prefillData.companyLand.trim().toLowerCase();
              const match = countries.find((country) => {
                const candidates = [country.Land, country.ISO, country.ISO2, country.ISO3, country.Nationality].filter(Boolean).map((candidate) => String(candidate).trim().toLowerCase());
                return candidates.includes(normalisedCountry);
              });

              if (match?.LandId) {
                nextValues.companyLandID = String(match.LandId);
              }
            }

            return nextValues;
          });

          const shouldOpen = [
            prefillData.STRAAT_NR,
            prefillData.WOONPL,
            prefillData.POSTNR,
            prefillData.companyOPMERKING,
            prefillData.companyGSM,
            prefillData.companyTEL,
            prefillData.DEPT,
            prefillData.GSM,
            prefillData.TEL,
            prefillData.OPMERKING,
            prefillData.businessType,
            prefillData.interestedIn,
            prefillData.otherBusinessType
          ].some((value) => {
            if (Array.isArray(value)) return value.length > 0;
            return typeof value === 'string' ? value.trim() !== '' : Boolean(value);
          });

          if (shouldOpen) {
            onOpen();
          }
        }, [prefillData, countries, onOpen, setValues]);

        return (
          <Form>
            <Container py={{ base: '4', md: '8' }} px={{ base: '4', md: '8' }}>
              <Stack spacing="5">
                <Heading size="xl">General information</Heading>
              </Stack>

              <Stack direction={{ base: 'column', lg: 'row' }} spacing={{ base: '5', lg: '8' }} justify="space-between">
                <Box bg="bg-surface" borderRadius="lg" flex="1">
                  <Stack spacing="5" py={{ base: '5', md: '6' }}>
                    <TextInput isRequired aria-required="true" autoFocus label="Your name (first and last name)" name="NAAM" type="text" />
                    <TextInput isRequired aria-required="true" label="E-Mail" name="E-mail" type="text" />
                    <SelectInput
                      isRequired
                      aria-required="true"
                      label="Country"
                      name="companyLandID"
                      as="select"
                      options={countries.map((count) => (
                        <option key={count.LandId} value={count.LandId}>
                          {count.Land}
                        </option>
                      ))}
                    />
                    <TextInput label="Website" name="web-site" type="text" />

                    <CheckboxInput
                      label="Interested in"
                      name="interestedIn"
                      handleChange={(e) => {
                        if (e.target.checked) {
                          formikProps.setFieldValue('interestedIn', [...formikProps.values.interestedIn, e.target.id]);
                        } else {
                          formikProps.setFieldValue(
                            'interestedIn',
                            formikProps.values.interestedIn.filter((i) => i !== e.target.id)
                          );
                        }
                      }}
                    />

                    <Divider my={4} />

                    <BusinessTypeCheckboxInput
                      label="Type of business"
                      name="businessType"
                      handleChange={(e) => {
                        if (e.target.checked) {
                          formikProps.setFieldValue('businessType', [...formikProps.values.businessType, e.target.id]);
                        } else {
                          formikProps.setFieldValue(
                            'businessType',
                            formikProps.values.businessType.filter((i) => i !== e.target.id)
                          );
                        }
                      }}
                      setOtherValue={(value) => {
                        formikProps.setFieldValue('otherBusinessType', value);
                      }}
                    />

                    <Divider my={4} />

                    <SelectInput
                      label="I have talked to"
                      name="talkedTo"
                      as="select"
                      options={[
                        <option key="" value="">
                          Select a person...
                        </option>,
                        <option key="Jan" value="Jan">
                          Jan
                        </option>,
                        <option key="Stephen" value="Stephen">
                          Stephen
                        </option>,
                        <option key="Kim" value="Kim">
                          Kim
                        </option>,
                        <option key="Joren" value="Joren">
                          Joren
                        </option>,
                        <option key="Nelke" value="Nelke">
                          Nelke
                        </option>,
                        <option key="Matthias" value="Matthias">
                          Matthias
                        </option>,
                        <option key="Ingrid" value="Ingrid">
                          Ingrid
                        </option>
                      ]}
                    />

                    <Divider my={4} />

                    <TextareaInput label="Note" name="OPMERKING" type="text" />
                  </Stack>
                </Box>
              </Stack>
            </Container>

            <Container py={{ base: '4', md: '12' }} px={{ base: '4', md: '8' }}>
              <Flex spacing="5">
                <Heading size="xl">{isOpen ? '' : 'Some more information'}</Heading>
                <Spacer />
                <Button onClick={onToggle} aria-label={isOpen ? 'Hide more information' : 'Show more information'} {...fairOutlineButtonProps}>
                  <Icon as={isOpen ? FiChevronUp : FiChevronDown} />
                </Button>
              </Flex>

              <Collapse in={isOpen} animateOpacity>
                <Container py={{ base: '4', md: '8' }} px={{ base: '4', md: '8' }}>
                  <Stack spacing="5">
                    <Heading>Some information about your company</Heading>
                  </Stack>

                  <Stack direction={{ base: 'column', lg: 'row' }} spacing={{ base: '5', lg: '8' }} justify="space-between">
                    <Box bg="bg-surface" borderRadius="lg" flex="1">
                      <Stack spacing="5" py={{ base: '5', md: '6' }}>
                        <TextInput label="Street and number" name="STRAAT_NR" type="text" />
                        <Stack spacing="6" direction={{ base: 'column', md: 'row' }}>
                          <TextInput label="City" name="WOONPL" type="text" />
                          <TextInput label="ZIP" name="POSTNR" type="text" />
                        </Stack>

                        <Stack spacing="6" direction={{ base: 'column', md: 'row' }}>
                          <TextInput label="Workforce" name="personeelsbestand" type="text" />
                        </Stack>
                        <Stack spacing="6" direction={{ base: 'column', md: 'row' }}>
                          <TextInput label="Phone" name="companyGSM" type="text" />
                          <TextInput label="Mobile phone" name="companyTEL" type="text" />
                        </Stack>

                        <Stack>
                          <TextInput label="E-Mail" name="companyEmail" type="text" />
                        </Stack>
                        <Stack spacing="6" direction={{ base: 'column', md: 'row' }}>
                          <TextareaInput label="Note (please let us know your interests or questions)" name="companyOPMERKING" type="text" />
                        </Stack>
                        <Stack spacing="6" direction={{ base: 'column', md: 'row' }}></Stack>
                      </Stack>
                    </Box>
                  </Stack>
                </Container>

                <Container py={{ base: '4', md: '8' }} px={{ base: '4', md: '8' }}>
                  <Stack spacing="5">
                    <Heading>Some information about you</Heading>
                  </Stack>

                  <Stack direction={{ base: 'column', lg: 'row' }} spacing={{ base: '5', lg: '8' }} justify="space-between">
                    <Box bg="bg-surface" borderRadius="lg" flex="1">
                      <Stack spacing="5" py={{ base: '5', md: '6' }}>
                        <TextInput label="Function" name="DEPT" type="text" />
                        <Stack spacing="6" direction={{ base: 'column', md: 'row' }}>
                          <SelectInput label="Title" name="AanspreektitelID" as="select" options={titles} />
                          <SelectInput label="Language" name="TaalID" as="select" options={langEN} />
                        </Stack>

                        <Stack spacing="6" direction={{ base: 'column', md: 'row' }}>
                          <TextInput label="Phone" name="GSM" type="text" />
                          <TextInput label="Mobile phone" name="TEL" type="text" />
                        </Stack>
                      </Stack>
                    </Box>
                  </Stack>
                </Container>
              </Collapse>
            </Container>

            {SubmitButton}
          </Form>
        );
      }}
    </Formik>
  );
}
